import { useState, useEffect } from 'react';
import Header from '../../../components/Header';
import Footer from '../../../components/Footer';
import data from '../../../data/inteligencia-dados.json';

// JSON-LD Structured Data for Inteligência de Dados
const jsonLdData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Inteligência de Dados",
  "alternateName": "Business Intelligence e Data Analytics",
  "url": "https://www.targetsoftware.com.br/inteligencia-dados",
  "provider": {
    "@type": "Organization",
    "name": "Target Software",
    "url": "https://www.targetsoftware.com.br",
    "logo": "https://www.targetsoftware.com.br/assets/img/logo.png"
  },
  "description": "A solução de Inteligência de Dados da Target Software oferece dashboards personalizados, análises com IA, alertas automáticos, plano de ação integrado, construção de malhas de dados estruturadas e eliminação de controles paralelos.",
  "serviceType": "Business Intelligence, Data Analytics e Data Engineering",
  "areaServed": {
    "@type": "Country",
    "name": "Brasil"
  },
  "audience": {
    "@type": "BusinessAudience",
    "businessSize": "SMB/Enterprise",
    "industry": "Serviços Financeiros, Bebidas, Varejo, Indústria, Tecnologia"
  },
  "offers": {
    "@type": "Offer",
    "price": "Sob consulta",
    "priceCurrency": "BRL",
    "availability": "https://schema.org/InStock"
  },
  "brand": {
    "@type": "Brand",
    "name": "Target Software"
  },
  "hasPart": [
    {
      "@type": "Service",
      "name": "Sem custos adicionais",
      "description": "Escalabilidade da área de inteligência com otimização dos custos de ferramenta."
    },
    {
      "@type": "Service",
      "name": "Análise com IA",
      "description": "Geração automática de resumos, análises e insights por inteligência artificial."
    },
    {
      "@type": "Service",
      "name": "Notificação Automática",
      "description": "Alertas inteligentes e thresholds enviados via WhatsApp, e-mail, aplicativo ou outros canais."
    },
    {
      "@type": "Service",
      "name": "Plano de Ação",
      "description": "Criação de atividades vinculadas a indicadores com responsável, prazo e acompanhamento."
    },
    {
      "@type": "Service",
      "name": "Malhas de Dados",
      "description": "Construção de malhas de dados estruturadas em camadas raw, silver e gold."
    },
    {
      "@type": "Service",
      "name": "Eliminação de Controles Paralelos",
      "description": "Soluções integradas ao ambiente corporativo substituindo planilhas e controles manuais."
    }
  ],
  "keywords": [
    "business intelligence",
    "dashboards personalizados",
    "análise de dados",
    "inteligência de dados",
    "data analytics",
    "análise com IA",
    "alertas automáticos",
    "malhas de dados",
    "data engineering",
    "indicadores de performance",
    "BI corporativo",
    "relatórios automatizados"
  ],
  "image": "https://www.targetsoftware.com.br/assets/img/inteligencia-dados-banner.png"
};

const InteligenciaDadosPage = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);

  const { diferenciais, cases } = data;

  const handleContactClick = () => {
    window.location.href = '/contato';
  };

  // Add JSON-LD structured data to head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLdData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#8B5931] via-[#C98855] to-[#E2A678]">
        <div className="absolute inset-0">
          <img 
            src="/images/produtos/inteligencia-dados.jpg"
            alt="Inteligência de Dados"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#8B5931]/80 to-[#8B5931]/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-6">
              <i className="ri-database-2-line mr-2"></i>
              Dados e dashboards com alto desempenho
            </div>
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight mb-6">
              Inteligência de Dados
            </h1>
            <p className="text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
              Dashboard corporativo com IA e alertas automáticos <br/> 
              com <strong className="text-white"> alta performance</strong> em grandes volumes de dados <br/>
              perfeito para <strong className="text-white"> equipes internas e externas</strong> 
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Sem custo por usuário</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Crie plano de ação no dashboard</span>
              </div>
              <div className="flex items-center text-lg">
                <i className="ri-check-line text-green-400 mr-2"></i>
                <span>Alertas automáticos no whatsApp</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Saiba Mais - Vídeo */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Conheça a solução de <span className="text-[#8B5931]">Inteligência de Dados</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Descubra como nossa plataforma de inteligência de dados oferece uma alternativa 
            superior ao Power BI, com alta performance e integração total com qualquer ambiente.
          </p>
          <button
            onClick={() => setShowVideoModal(true)}
            className="inline-flex items-center px-8 py-4 bg-[#8B5931] text-white font-semibold rounded-xl hover:bg-[#6A4525] transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-play-circle-line text-2xl mr-3"></i>
            Assistir Vídeo de Apresentação
          </button>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Diferenciais Tecnológicos
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conheça os diferenciais que fazem da nossa solução a melhor escolha
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {diferenciais.map((diferencial, index) => (
              <div key={index} className="group">
                <div className="bg-gray-50 rounded-2xl p-6 h-full hover:bg-[#8B5931] transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer">
                  <div className="w-12 h-12 bg-[#8B5931] rounded-xl flex items-center justify-center mb-4 group-hover:bg-white transition-colors duration-300">
                    <i className={`${diferencial.icon} text-2xl text-white group-hover:text-[#8B5931]`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-white transition-colors duration-300">
                    {diferencial.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                    {diferencial.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cases de Sucesso */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Cases de Sucesso
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Grandes instituições já utilizam nossa Inteligência de Dados para otimizar suas operações
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {cases.map((caso, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center mb-6">
                  <img 
                    src={caso.logo} 
                    alt={caso.cliente}
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {caso.cliente}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {caso.descricao}
                </p>               
                <ul className="mt-4 space-y-2">
                  {caso.indicadores && caso.indicadores.map((indicador, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 text-sm font-medium">
                      <i className="ri-check-line text-green-500 mr-2"></i>
                      {indicador}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-[#8B5931]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Pronto para evoluir sua área informacional?
          </h2>
          <p className="text-xl text-gray-200 mb-8 leading-relaxed">
            Entre em contato conosco e descubra como nossa plataforma pode transformar 
            a gestão de dados e performance da sua empresa.
          </p>
          <button
            onClick={handleContactClick}
            className="inline-flex items-center px-8 py-4 bg-white text-[#8B5931] font-semibold rounded-xl hover:bg-gray-100 transition-all duration-300 hover:scale-105 hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line text-xl mr-3"></i>
            Falar com Especialista
          </button>
        </div>
      </section>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Vídeo de Apresentação</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-gray-100 rounded-xl p-8 text-center">
              <i className="ri-video-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">
                O vídeo de apresentação será disponibilizado em breve. 
                Entre em contato para mais informações.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default InteligenciaDadosPage;